import React, { ReactElement } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import rehypeWrap from 'rehype-wrap-all'
import rehypeTagNameChange from '@/util/rehype-custom-plugins/rehype-tag-swap'
import { ClientAgentEventRead } from '../../store/schemas'
import AgentResponseCitationPrintable from './AgentResponseCitationPrintable'
import { readAgentCitationSpanProps, rehypeAgentCitations } from '@/util/rehype-custom-plugins/rehype-agent-citations'
import { getCitationLabelFromFootnoteInfo, type FootnoteInfoByCitationId } from '@/agent/references/util/agent-reference-footnotes'

interface EventTextMarkdownPrintableProps {
  event: ClientAgentEventRead
  footnoteInfoByCitationId?: FootnoteInfoByCitationId
}

/**
 * Event Text Markdown Printable
 *
 * Renders the event’s value (text response) as Markdown suitable for printing or copying to the clipboard.
 */
const EventTextMarkdownPrintable: React.FC<EventTextMarkdownPrintableProps> = (props: EventTextMarkdownPrintableProps): ReactElement | null => {
  const { event, footnoteInfoByCitationId } = props

  if (!event || !event.value) {
    console.error('EventTextMarkdownPrintable: Event or event value is missing.')
    return null
  }

  // Trim whitespace from the event's text
  const trimmedText = event.value.trim()

  return (
    <Markdown
      className="markdown-bubble-container max-w-3xl"
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[
        rehypeRaw,
        [rehypeWrap, { selector: 'table', wrapper: 'div.source-html-table-wrapper' }],
        rehypeAgentCitations,
        [
          // Swap <pre> and <code> tags with <div> tags for consistent formatting in printable output
          rehypeTagNameChange,
          [
            { tagName: 'pre', newTagName: 'div' },
            { tagName: 'code', newTagName: 'div' },
          ],
        ],
      ]}
      components={{
        // Handle divs
        div: ({ children, ...props }) => {
          // map the children and if there is a child string that contains any more than 2 '/n' for example
          // "\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n" then repalce it with a max of a 2 new line '\n\n'
          const newLineRegex = /\n{2,}/g
          const filteredChildren = Array.isArray(children)
            ? children.map((child) => {
                if (typeof child === 'string') {
                  return child.replace(newLineRegex, '\n\n')
                }
                return child
              })
            : children

          return <div {...props}>{filteredChildren}</div>
        },

        // Handle list items
        li: ({ children, ...props }) => {
          // filter the children and if there is a child string of '\n' then remove it
          const filteredChildren = Array.isArray(children) ? children.filter((child) => child !== '\n') : children

          return <li {...props}>{filteredChildren}</li>
        },

        // Handle citation spans
        span: ({ node, ...props }) => {
          const citation = readAgentCitationSpanProps(props)
          if (!citation) return <span {...props}>{props.children}</span>

          if (!footnoteInfoByCitationId) {
            console.error('EventTextMarkdownPrintable: citation span found but footnoteInfoByCitationId is missing.')
          }
          const citationLabel = footnoteInfoByCitationId
            ? getCitationLabelFromFootnoteInfo(footnoteInfoByCitationId[citation.citationId], citation.citationIdIndex)
            : undefined

          if (citation.isLegalCitationLink) {
            // The authority text is part of the prose and must survive print/copy.
            // When the citation resolves, its footnote badge follows the text.
            if (!citationLabel) return <>{props.children}</>
            return (
              <>
                {props.children} <AgentResponseCitationPrintable citationLabel={citationLabel} />
              </>
            )
          }

          // Reference marker: renders as its badge alone; hidden while unresolved —
          // the span children are the raw citation id, which must never reach print/copy.
          // Deliberate divergence from chat, which shows a grey [?] "Not linked" badge
          // here: exported/copied documents omit unresolved citations entirely.
          if (!citationLabel) return null
          return <AgentResponseCitationPrintable citationLabel={citationLabel} />
        },
      }}
    >
      {trimmedText}
    </Markdown>
  )
}

export default EventTextMarkdownPrintable
